import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { 
  Shield, Activity, Lock, Users, FileText, Crosshair, Zap, Server,
  Menu, X, ChevronRight, Target, AlertTriangle, TrendingDown,
  Building2, Star, Landmark, Handshake, Download, Mail, ExternalLink,
  Clock, Home, Scale, BookOpen, GraduationCap, Newspaper, Heart
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Toaster, toast } from "sonner";

// Logo URL from assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_guard-defender-net/artifacts/bzgg2ntx_CI_Dark_ShieldOnly.png";

// Background images
const HERO_BG = "https://images.pexels.com/photos/10964248/pexels-photo-10964248.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ADVOCACY_BG = "https://images.unsplash.com/photo-1764004657175-11ce991efcca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxjYXBpdG9sJTIwYnVpbGRpbmclMjBuaWdodHxlbnwwfHx8fDE3NzUyNTk3NTZ8MA&ixlib=rb-4.1.0&q=85";

// Navigation Component
const Navigation = ({ activeSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { href: "#threat", label: "The Threat" },
    { href: "#solution", label: "Our Solution" },
    { href: "#partnership", label: "Partnership" },
    { href: "#policy", label: "Policy" },
    { href: "#resources", label: "Resources" },
    { href: "#get-involved", label: "Get Involved" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-[#0D1B2A]/95 backdrop-blur-xl border-b border-[#8BA4BE]/20" 
          : "bg-[#0D1B2A]/85 backdrop-blur-xl"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a 
            href="#" 
            className="flex items-center gap-3"
            data-testid="nav-logo"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <img src={LOGO_URL} alt="Critical Guard" className="h-10 md:h-12 w-auto" />
            <div className="hidden sm:block">
              <div className="font-outfit font-black text-white text-sm md:text-base tracking-wider">CRITICAL GUARD</div>
              <div className="text-[#8BA4BE] text-[10px] md:text-xs tracking-wide">Digital National Guard Framework</div>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={(e) => scrollToSection(e, link.href)}
                className={`text-xs xl:text-sm font-medium uppercase tracking-wider transition-colors ${
                  activeSection === link.href.replace('#', '')
                    ? "text-[#F5A623]"
                    : "text-white hover:text-[#F5A623]"
                }`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="#get-involved"
              data-testid="nav-cta-register"
              onClick={(e) => scrollToSection(e, "#get-involved")}
              className="inline-flex items-center justify-center px-5 py-2.5 bg-[#F5A623] text-[#0D1B2A] font-bold text-xs uppercase tracking-wider hover:bg-[#F5A623]/90 hover:-translate-y-0.5 transition-all rounded-none"
            >
              Register Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-white"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#0D1B2A] border-t border-[#8BA4BE]/20 py-4" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="block py-3 px-4 text-sm font-medium uppercase tracking-wider text-white hover:text-[#F5A623] hover:bg-[#162032] transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="px-4 pt-4">
              <a
                href="#get-involved"
                onClick={(e) => scrollToSection(e, "#get-involved")}
                className="block w-full text-center py-3 bg-[#F5A623] text-[#0D1B2A] font-bold text-sm uppercase tracking-wider"
              >
                Register Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Threat Status Card Component
const ThreatStatusCard = () => {
  return (
    <div 
      data-testid="threat-status-card"
      className="backdrop-blur-xl bg-[#162032]/80 border border-[#2A9D8F]/40 p-6 md:p-8 rounded-none shadow-[0_0_40px_rgba(42,157,143,0.15)] relative"
    >
      <div className="flex items-center gap-3 mb-4">
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
        </span>
        <span className="font-mono text-red-400 font-bold text-sm tracking-wider">THREAT STATUS: ELEVATED</span>
      </div>
      
      <div className="border-t border-[#8BA4BE]/20 my-4"></div>
      
      <div className="font-mono text-[#8BA4BE] text-xs space-y-1 mb-4">
        <p className="text-white font-semibold">Active Foreign Campaigns:</p>
        <p className="pl-4">• Water Treatment Networks</p>
        <p className="pl-4">• Regional Power Grids</p>
        <p className="pl-4">• Emergency Services IT</p>
      </div>
      
      <div className="border-t border-[#8BA4BE]/20 my-4"></div>
      
      <div className="font-mono text-xs">
        <p className="text-[#F5A623] font-semibold mb-2">Current Coverage Gap:</p>
        <p className="text-white">
          <span className="text-4xl font-black text-[#F5A623]">90%</span>
          <span className="text-[#8BA4BE] ml-2">of local operators</span>
        </p>
        <p className="text-[#8BA4BE]">have no federal defense pathway activated.</p>
      </div>
      
      <div className="border-t border-[#8BA4BE]/20 my-4"></div>
      
      <div className="flex items-center gap-2">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2A9D8F] opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2A9D8F]"></span>
        </span>
        <span className="font-mono text-[#2A9D8F] text-xs font-bold tracking-wider">Critical Guard Status: ACTIVE</span>
      </div>
    </div>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section 
      id="hero" 
      data-testid="hero-section"
      className="min-h-screen flex items-center pt-20 relative overflow-hidden"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 z-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      ></div>
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0D1B2A] via-[#0D1B2A]/95 to-[#0D1B2A]/70 z-10"></div>
      
      {/* Circuit Pattern Overlay */}
      <div className="absolute inset-0 z-10 opacity-5 circuit-pattern"></div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center">
          {/* Left Content - 3 columns */}
          <div className="lg:col-span-3 animate-fade-in">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F5A623]/10 border border-[#F5A623]/30 mb-8">
              <Shield className="w-4 h-4 text-[#F5A623]" />
              <span className="text-[#F5A623] text-xs font-bold uppercase tracking-wider">
                Nonprofit · National Security · Digital National Guard Framework
              </span>
            </div>
            
            {/* Headline */}
            <h1 className="font-outfit font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white tracking-tighter leading-[1.05] mb-6">
              LOCAL<br />
              INFRASTRUCTURE.<br />
              NATIONAL<br />
              <span className="text-[#F5A623]">STRENGTH.</span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-[#8BA4BE] text-base md:text-lg leading-relaxed max-w-xl mb-8">
              Foreign adversaries are targeting your water, power, and transit systems right now. 
              Critical Guard deploys elite National Guard cyber defenders — in hours, not weeks — 
              to protect the communities that keep America running.
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a
                href="#get-involved"
                data-testid="hero-cta-register"
                onClick={(e) => scrollToSection(e, "#get-involved")}
                className="inline-flex items-center justify-center px-8 py-4 bg-[#F5A623] text-[#0D1B2A] font-extrabold text-sm uppercase tracking-wider hover:bg-[#F5A623]/90 hover:-translate-y-1 transition-all rounded-none"
              >
                Register Your Infrastructure
                <ChevronRight className="ml-2 w-4 h-4" />
              </a>
              <a
                href="#solution"
                data-testid="hero-cta-learn"
                onClick={(e) => scrollToSection(e, "#solution")}
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent border border-[#8BA4BE]/40 text-white font-bold text-sm uppercase tracking-wider hover:bg-[#8BA4BE]/10 hover:border-[#8BA4BE] transition-all rounded-none"
              >
                Learn How It Works
              </a>
            </div>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4 md:gap-6">
              <div className="flex items-center gap-2 text-[#8BA4BE] text-xs md:text-sm">
                <Zap className="w-4 h-4 text-[#F5A623]" />
                <span>Title 32 Authority</span>
              </div>
              <div className="flex items-center gap-2 text-[#8BA4BE] text-xs md:text-sm">
                <Landmark className="w-4 h-4 text-[#2A9D8F]" />
                <span>NSA-Backed Intelligence</span>
              </div>
              <div className="flex items-center gap-2 text-[#8BA4BE] text-xs md:text-sm">
                <Clock className="w-4 h-4 text-[#F5A623]" />
                <span>Hours, Not Weeks</span>
              </div>
            </div>
          </div>
          
          {/* Right Content - Threat Card - 2 columns */}
          <div className="lg:col-span-2 animate-fade-in-delay">
            <ThreatStatusCard />
          </div>
        </div>
      </div>
    </section>
  );
};

// Impact Statistics Bar
const ImpactStatsBar = () => {
  const stats = [
    { value: "16", label: "Federally Designated CI Sectors" },
    { value: "Weeks → Hours", label: "Deployment Time Reduction" },
    { value: "90%", label: "Of Local Operators Currently Undefended" },
    { value: "$0", label: "New Federal Spending Required" },
  ];

  return (
    <section data-testid="impact-stats-section" className="bg-[#162032] border-t-2 border-[#F5A623] py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
          {stats.map((stat, index) => (
            <div 
              key={index} 
              className={`text-center ${index < stats.length - 1 ? "md:border-r md:border-[#8BA4BE]/20" : ""}`}
            >
              <div className="text-3xl md:text-4xl lg:text-5xl font-black text-[#F5A623] mb-2 font-outfit">
                {stat.value}
              </div>
              <div className="text-[#8BA4BE] text-xs md:text-sm uppercase tracking-wider">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Problem/Threat Section
const ProblemSection = () => {
  const threatCards = [
    {
      icon: Target,
      title: "The Target",
      description: "Municipal \"soft targets\" — water treatment, power grids, transit systems — have critical community safety implications but limited cyber budgets and no pathway to federal defense.",
      borderColor: "border-t-[#2A9D8F]",
    },
    {
      icon: AlertTriangle,
      title: "The Threat",
      description: "Highly resourced, coordinated nation-state campaigns from China, Russia, Iran, and North Korea — designed to disrupt, degrade, and pre-position in American critical infrastructure.",
      borderColor: "border-t-[#E63946]",
    },
    {
      icon: TrendingDown,
      title: "The Result",
      description: "A disparity gap where rural and underfunded municipalities become systemic national vulnerabilities — the same soft-target vector exploited in every major foreign cyber campaign.",
      borderColor: "border-t-[#F5A623]",
    },
  ];

  return (
    <section id="threat" data-testid="threat-section" className="bg-[#F4F7FA] py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="text-[#2A9D8F] text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
            Understanding the Threat
          </span>
          <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-bold text-[#0D1B2A] tracking-tight mb-6">
            The Front Lines Have Shifted to Local Infrastructure
          </h2>
          <p className="text-[#162032] text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            State and local critical infrastructure are under constant assault from sophisticated 
            foreign adversaries — yet lack the expertise and federal intelligence to detect them.
          </p>
        </div>

        {/* Threat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-16">
          {threatCards.map((card, index) => (
            <div
              key={index}
              data-testid={`threat-card-${index}`}
              className={`bg-[#0D1B2A] border border-[#8BA4BE]/15 ${card.borderColor} border-t-4 p-8 rounded-none hover:shadow-lg transition-all group`}
            >
              <card.icon className="w-10 h-10 text-[#F5A623] mb-4" />
              <h3 className="font-outfit text-xl font-bold text-white mb-4">{card.title}</h3>
              <p className="text-[#8BA4BE] text-sm leading-relaxed">{card.description}</p>
            </div>
          ))}
        </div>

        {/* Bottleneck Explainer */}
        <div className="bg-white border border-[#8BA4BE]/20 p-8 md:p-12 rounded-none">
          <h3 className="font-outfit text-xl md:text-2xl font-bold text-[#0D1B2A] text-center mb-8">
            The Traditional DSCA Process
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-6 bg-[#F4F7FA] border border-[#8BA4BE]/20">
              <div className="text-[#2A9D8F] text-xs font-bold uppercase tracking-wider mb-3">Step 1</div>
              <h4 className="font-bold text-[#0D1B2A] mb-2">Local & State Exhaustion</h4>
              <p className="text-[#162032] text-sm">Local entity overwhelmed → requests State Emergency Management → State Guard deployed</p>
            </div>
            <div className="text-center p-6 bg-[#F4F7FA] border border-[#8BA4BE]/20">
              <div className="text-[#2A9D8F] text-xs font-bold uppercase tracking-wider mb-3">Step 2</div>
              <h4 className="font-bold text-[#0D1B2A] mb-2">Federal Validation</h4>
              <p className="text-[#162032] text-sm">State requests Lead Federal Agency (FEMA/DHS) support via formal Request for Assistance</p>
            </div>
            <div className="text-center p-6 bg-[#F4F7FA] border border-[#8BA4BE]/20">
              <div className="text-[#2A9D8F] text-xs font-bold uppercase tracking-wider mb-3">Step 3</div>
              <h4 className="font-bold text-[#0D1B2A] mb-2">The SecDef Gate</h4>
              <p className="text-[#162032] text-sm">Mission Assignment forwarded to DoD — requires Secretary of Defense validation for legality</p>
            </div>
          </div>

          {/* Alert Banner */}
          <div className="bg-[#E63946]/10 border border-[#E63946]/30 p-4 md:p-6 flex items-center gap-4">
            <AlertTriangle className="w-6 h-6 text-[#E63946] flex-shrink-0" />
            <p className="text-[#E63946] font-bold text-sm md:text-base">
              THE PROBLEM: This linear, manual process takes weeks. Nation-state cyber attacks execute in milliseconds.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Solution Section
const SolutionSection = () => {
  const steps = [
    {
      step: "01",
      title: "Register (Automated DSCA)",
      description: "CI operators register their networks. The platform streamlines the DSCA paperwork into a rapid authorization package — the bureaucratic bottleneck, eliminated.",
    },
    {
      step: "02",
      title: "Guard Units Bid",
      description: "Title 32-funded Cyber Protection Teams register their availability and technical specialties — ICS/SCADA systems, network types, threat hunt experience.",
    },
    {
      step: "03",
      title: "Intelligence Sync",
      description: "The platform maps federal hunting mission packages to the target network, syncing with NSA/CISA Title 10/50 intelligence to ensure units hunt against current adversary signatures.",
    },
    {
      step: "04",
      title: "Smart Matching",
      description: "Our system pairs the operator's specific needs with the right Guard unit and the right hunt package. Response time drops from weeks to hours.",
    },
  ];

  const features = [
    {
      icon: Clock,
      title: "Speed",
      description: "Governor-activated in hours, bypassing weeks of federal mobilization",
    },
    {
      icon: Home,
      title: "Local Knowledge",
      description: "Embedded local tech and utility workers serving in uniform",
    },
    {
      icon: Scale,
      title: "Legal Freedom",
      description: "Exempt from Posse Comitatus constraints under Title 32 State Active Duty",
    },
  ];

  return (
    <section id="solution" data-testid="solution-section" className="bg-[#0D1B2A] py-20 md:py-32 relative overflow-hidden">
      {/* Circuit Pattern */}
      <div className="absolute inset-0 opacity-5 circuit-pattern"></div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="text-[#F5A623] text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
            The Solution
          </span>
          <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
            Critical Guard: The Connection Layer America Needs
          </h2>
          <p className="text-[#8BA4BE] text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            We don't create new resources. We compress weeks of bureaucratic friction to align 
            existing, siloed national strengths into an immediate, localized defense.
          </p>
        </div>

        {/* Alignment Component */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="bg-[#162032] border border-[#2A9D8F]/40 p-8 rounded-none text-center">
            <Landmark className="w-12 h-12 text-[#2A9D8F] mx-auto mb-4" />
            <h3 className="font-outfit text-lg font-bold text-white mb-3">Federal Intelligence (Title 50)</h3>
            <p className="text-[#8BA4BE] text-sm">Real-time NSA/CISA foreign adversary signatures and threat hunt packages</p>
          </div>
          
          <div className="bg-[#F5A623]/10 border-2 border-[#F5A623] p-8 rounded-none text-center transform md:-translate-y-4">
            <Shield className="w-12 h-12 text-[#F5A623] mx-auto mb-4" />
            <h3 className="font-outfit text-lg font-bold text-white mb-3">Critical Guard Framework</h3>
            <p className="text-[#8BA4BE] text-sm">The automated routing and matchmaking engine that connects the three</p>
          </div>
          
          <div className="bg-[#162032] border border-[#F5A623]/40 p-8 rounded-none text-center">
            <Star className="w-12 h-12 text-[#F5A623] mx-auto mb-4" />
            <h3 className="font-outfit text-lg font-bold text-white mb-3">National Guard CPTs (Title 32)</h3>
            <p className="text-[#8BA4BE] text-sm">Elite, locally-commanded Cyber Protection Teams — federally trained, state-directed</p>
          </div>
        </div>

        {/* How the Marketplace Works */}
        <div className="mb-16">
          <h3 className="font-outfit text-2xl font-bold text-white text-center mb-10">How the Marketplace Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {steps.map((item, index) => (
              <div 
                key={index}
                data-testid={`solution-step-${index}`}
                className="bg-[#162032] border border-[#8BA4BE]/15 p-8 rounded-none hover:border-[#F5A623]/50 transition-colors group"
              >
                <div className="text-[#F5A623] text-4xl font-black font-outfit mb-4">{item.step}</div>
                <h4 className="font-outfit text-lg font-bold text-white mb-3">{item.title}</h4>
                <p className="text-[#8BA4BE] text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Result Callout */}
        <div className="bg-[#F5A623]/10 border border-[#F5A623]/40 p-6 md:p-8 mb-16">
          <div className="flex items-center justify-center gap-6 flex-wrap">
            <span className="flex items-center gap-2 text-[#F5A623] font-bold">
              <Zap className="w-5 h-5" />
              Rapid Response
            </span>
            <span className="text-[#8BA4BE]">·</span>
            <span className="text-white font-bold">Erasing the Disparity Gap</span>
            <span className="text-[#8BA4BE]">·</span>
            <span className="text-white font-bold">National Standardization</span>
          </div>
        </div>

        {/* Why National Guard */}
        <div>
          <h3 className="font-outfit text-2xl font-bold text-white text-center mb-10">Why the National Guard?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#2A9D8F]/20 border border-[#2A9D8F]/40 rounded-none flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-[#2A9D8F]" />
                </div>
                <h4 className="font-outfit text-lg font-bold text-white mb-2">{feature.title}</h4>
                <p className="text-[#8BA4BE] text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Stakeholder Pathways Section
const StakeholderSection = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentForm, setCurrentForm] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    message: "",
  });

  const stakeholders = [
    {
      icon: Building2,
      title: "Local Infrastructure Operators",
      audience: "Municipal Power & Water · Transit · Emergency Services · Dams · Healthcare",
      description: "Direct access to a marketplace that matches them with specialized Guard cyber defense teams, funded by the State or Federal government. PCII-protected data handling.",
      cta: "Register Your Infrastructure →",
      ctaColor: "bg-[#2A9D8F] hover:bg-[#2A9D8F]/90",
      borderColor: "border-l-[#2A9D8F]",
      formType: "infrastructure",
    },
    {
      icon: Star,
      title: "National Guard Cyber Units",
      audience: "State & Air National Guard CPTs · Title 32 Authorized Units",
      description: "A force-multiplier platform that identifies high-priority real-world mission opportunities, synced with current federal threat intelligence and hunt packages.",
      cta: "Register Your Unit →",
      ctaColor: "bg-[#F5A623] hover:bg-[#F5A623]/90 text-[#0D1B2A]",
      borderColor: "border-l-[#F5A623]",
      formType: "unit",
    },
    {
      icon: Landmark,
      title: "State & Federal Government",
      audience: "Governors · Adjutants General · State CISOs · CISA · NSA · DoD",
      description: "National visibility into local network vulnerabilities. Automated DSCA routing that activates state cyber assets faster than any existing pathway.",
      cta: "Schedule a Briefing →",
      ctaColor: "bg-[#0D1B2A] hover:bg-[#162032] border border-[#8BA4BE]/40",
      borderColor: "border-l-[#0D1B2A]",
      formType: "government",
    },
    {
      icon: Handshake,
      title: "Private Sector & Philanthropic Partners",
      audience: "Technology Companies · Foundations · Industry Associations · Researchers",
      description: "A mission-aligned partnership with the leading framework for domestic critical infrastructure cyber defense. Technology integration opportunities.",
      cta: "Become a Partner →",
      ctaColor: "bg-[#E63946] hover:bg-[#E63946]/90",
      borderColor: "border-l-[#E63946]",
      formType: "partner",
    },
  ];

  const ecosystemBadges = ["CISA", "NSA", "U.S. CYBERCOM", "DHS", "FEMA", "ISACs"];

  const handleOpenModal = (formType) => {
    setCurrentForm(formType);
    setFormSubmitted(false);
    setFormData({ name: "", email: "", organization: "", message: "" });
    setModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate form submission
    setFormSubmitted(true);
    toast.success("Form submitted successfully! We'll be in touch soon.");
  };

  const getFormTitle = () => {
    switch (currentForm) {
      case "infrastructure": return "Register Your Infrastructure";
      case "unit": return "Register Your Unit";
      case "government": return "Schedule a Briefing";
      case "partner": return "Become a Partner";
      default: return "Contact Us";
    }
  };

  return (
    <section id="partnership" data-testid="partnership-section" className="bg-[#F4F7FA] py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="text-[#2A9D8F] text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
            Join the Mission
          </span>
          <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-bold text-[#0D1B2A] tracking-tight mb-6">
            The Partnership Network
          </h2>
          <p className="text-[#162032] text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Critical Guard aligns incentives at every level — local, state, and federal. 
            Find your role in defending America's infrastructure.
          </p>
        </div>

        {/* Stakeholder Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12">
          {stakeholders.map((stakeholder, index) => (
            <div
              key={index}
              data-testid={`stakeholder-card-${index}`}
              className={`bg-white border border-[#8BA4BE]/20 ${stakeholder.borderColor} border-l-4 p-8 rounded-none shadow-sm hover:shadow-md hover:-translate-y-1 transition-all`}
            >
              <stakeholder.icon className="w-10 h-10 text-[#0D1B2A] mb-4" />
              <h3 className="font-outfit text-xl font-bold text-[#0D1B2A] mb-2">{stakeholder.title}</h3>
              <p className="text-[#2A9D8F] text-xs font-semibold uppercase tracking-wider mb-4">{stakeholder.audience}</p>
              <p className="text-[#162032] text-sm leading-relaxed mb-6">{stakeholder.description}</p>
              <button
                onClick={() => handleOpenModal(stakeholder.formType)}
                data-testid={`stakeholder-cta-${index}`}
                className={`inline-flex items-center justify-center px-6 py-3 ${stakeholder.ctaColor} text-white font-bold text-sm uppercase tracking-wider rounded-none transition-all`}
              >
                {stakeholder.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Ecosystem Badges */}
        <div className="flex flex-wrap justify-center gap-4">
          {ecosystemBadges.map((badge, index) => (
            <div
              key={index}
              className="px-6 py-3 bg-[#0D1B2A] text-white text-xs font-bold uppercase tracking-wider"
            >
              {badge}
            </div>
          ))}
        </div>
      </div>

      {/* Form Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#0D1B2A] border border-[#8BA4BE]/30 text-white max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-outfit text-2xl font-bold text-white">{getFormTitle()}</DialogTitle>
            <DialogDescription className="text-[#8BA4BE]">
              Fill out the form below and we'll get back to you shortly.
            </DialogDescription>
          </DialogHeader>
          
          {formSubmitted ? (
            <div className="py-8 text-center" data-testid="form-success-message">
              <div className="w-16 h-16 bg-[#2A9D8F]/20 border border-[#2A9D8F] rounded-none flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#2A9D8F]" />
              </div>
              <h3 className="font-outfit text-xl font-bold text-white mb-2">Thank You!</h3>
              <p className="text-[#8BA4BE]">Your submission has been received. We'll be in touch soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div>
                <label className="text-sm font-bold uppercase tracking-wider text-[#8BA4BE] mb-2 block">Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-[#162032] border border-[#8BA4BE]/30 text-white placeholder:text-[#8BA4BE]/50 focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623] rounded-none p-4"
                  placeholder="Your name"
                  data-testid="form-input-name"
                />
              </div>
              <div>
                <label className="text-sm font-bold uppercase tracking-wider text-[#8BA4BE] mb-2 block">Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-[#162032] border border-[#8BA4BE]/30 text-white placeholder:text-[#8BA4BE]/50 focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623] rounded-none p-4"
                  placeholder="your@email.com"
                  data-testid="form-input-email"
                />
              </div>
              <div>
                <label className="text-sm font-bold uppercase tracking-wider text-[#8BA4BE] mb-2 block">Organization</label>
                <input
                  type="text"
                  required
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  className="w-full bg-[#162032] border border-[#8BA4BE]/30 text-white placeholder:text-[#8BA4BE]/50 focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623] rounded-none p-4"
                  placeholder="Your organization"
                  data-testid="form-input-organization"
                />
              </div>
              <div>
                <label className="text-sm font-bold uppercase tracking-wider text-[#8BA4BE] mb-2 block">Message</label>
                <textarea
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full bg-[#162032] border border-[#8BA4BE]/30 text-white placeholder:text-[#8BA4BE]/50 focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623] rounded-none p-4 resize-none"
                  placeholder="Tell us more about your needs..."
                  data-testid="form-input-message"
                />
              </div>
              <button
                type="submit"
                data-testid="form-submit-button"
                className="w-full inline-flex items-center justify-center px-8 py-4 bg-[#F5A623] text-[#0D1B2A] font-extrabold text-sm uppercase tracking-wider hover:bg-[#F5A623]/90 transition-all rounded-none"
              >
                Submit
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

// Policy Section
const PolicySection = () => {
  const priorities = [
    {
      title: "Formalize the Digital National Guard Framework",
      description: "Authorize a statutory pilot program that creates a fast-lane DSCA pathway for cyber missions — bypassing the SecDef Gate for pre-authorized Guard CPT deployments to registered CI operators.",
    },
    {
      title: "Streamline DSCA for Cyber",
      description: "Update Defense Support of Civil Authorities regulations to treat cyber threat-hunting missions differently from traditional military support — enabling proactive defense, not just reactive incident response.",
    },
    {
      title: "Activate CIRCIA as a Response Pipeline",
      description: "Leverage the Cyber Incident Reporting for Critical Infrastructure Act (CIRCIA) reporting requirements to create a direct reporting-to-response pipeline connecting CI operators to Guard cyber units.",
    },
  ];

  return (
    <section 
      id="policy" 
      data-testid="policy-section" 
      className="py-20 md:py-32 relative overflow-hidden"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 z-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${ADVOCACY_BG})` }}
      ></div>
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-[#0D1B2A]/90 z-10"></div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-20">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="text-[#F5A623] text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
            Policy & Advocacy
          </span>
          <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
            The Legislative Case for the Digital National Guard
          </h2>
          <p className="text-[#8BA4BE] text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            The framework works. The legal authority exists. What's needed is the political will 
            to activate it at scale. Here's how you can help.
          </p>
        </div>

        {/* Policy Priority Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {priorities.map((priority, index) => (
            <div
              key={index}
              data-testid={`policy-card-${index}`}
              className="bg-[#162032]/80 backdrop-blur-sm border border-[#F5A623]/30 p-8 rounded-none"
            >
              <div className="text-[#F5A623] text-sm font-bold uppercase tracking-wider mb-4">Priority {index + 1}</div>
              <h3 className="font-outfit text-lg font-bold text-white mb-4">{priority.title}</h3>
              <p className="text-[#8BA4BE] text-sm leading-relaxed">{priority.description}</p>
            </div>
          ))}
        </div>

        {/* Bipartisan Argument */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          <div className="bg-[#162032]/80 backdrop-blur-sm border border-[#8BA4BE]/20 p-8 rounded-none">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-8 h-8 text-[#F5A623]" />
              <h3 className="font-outfit text-xl font-bold text-white">The National Security Case</h3>
            </div>
            <p className="text-[#8BA4BE] text-sm leading-relaxed">
              "Foreign adversaries are already positioned inside American infrastructure. Every day without 
              a coordinated defense is a day our adversaries exploit the gap. Title 32 Guard units are 
              trained, funded, and ready — they simply need the administrative pathway to act."
            </p>
          </div>
          <div className="bg-[#162032]/80 backdrop-blur-sm border border-[#8BA4BE]/20 p-8 rounded-none">
            <div className="flex items-center gap-3 mb-4">
              <Home className="w-8 h-8 text-[#2A9D8F]" />
              <h3 className="font-outfit text-xl font-bold text-white">The Equity Case</h3>
            </div>
            <p className="text-[#8BA4BE] text-sm leading-relaxed">
              "A water treatment plant in rural Iowa deserves the same quality of cyber defense as a major 
              metropolitan utility. The disparity gap is not a resource problem — it's a coordination problem. 
              Critical Guard solves it."
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h3 className="font-outfit text-2xl font-bold text-white mb-8">Take Action Today</h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              data-testid="policy-cta-toolkit"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#F5A623] text-[#0D1B2A] font-extrabold text-sm uppercase tracking-wider hover:bg-[#F5A623]/90 hover:-translate-y-1 transition-all rounded-none"
            >
              <Download className="mr-2 w-4 h-4" />
              Download Advocacy Toolkit
            </button>
            <button
              data-testid="policy-cta-lawmakers"
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent border border-[#8BA4BE]/40 text-white font-bold text-sm uppercase tracking-wider hover:bg-[#8BA4BE]/10 hover:border-[#8BA4BE] transition-all rounded-none"
            >
              <Mail className="mr-2 w-4 h-4" />
              Contact Your Lawmakers
            </button>
            <button
              data-testid="policy-cta-briefing"
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent border border-[#8BA4BE]/40 text-white font-bold text-sm uppercase tracking-wider hover:bg-[#8BA4BE]/10 hover:border-[#8BA4BE] transition-all rounded-none"
            >
              <ExternalLink className="mr-2 w-4 h-4" />
              Request Congressional Briefing
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Resources Section
const ResourcesSection = () => {
  const resources = [
    {
      icon: FileText,
      title: "Strategic Blueprint",
      description: "The full Digital National Guard Framework — 18-page visual strategic document",
      cta: "Download PDF →",
    },
    {
      icon: Scale,
      title: "Legal Framework",
      description: "Title 10 vs. 32 vs. 50 · DSCA · EMAC · PCII Program — plain-language explainers",
      cta: "Read Explainers →",
    },
    {
      icon: Server,
      title: "Sector Guides",
      description: "All 16 federally designated CI sectors — threat vectors, SRMA contacts, ISAC directories",
      cta: "Explore Sectors →",
    },
    {
      icon: BookOpen,
      title: "For Policymakers",
      description: "One-pagers, fact sheets, and advocacy materials for congressional offices and state officials",
      cta: "Download Materials →",
    },
    {
      icon: GraduationCap,
      title: "CPT Standards",
      description: "U.S. Cyber Command training pipeline, DoD 8140/DCWF competency framework, certification pathways",
      cta: "Learn More →",
    },
    {
      icon: Newspaper,
      title: "News & Research",
      description: "Media coverage, press releases, research partnerships, and thought leadership",
      cta: "Browse →",
    },
  ];

  return (
    <section id="resources" data-testid="resources-section" className="bg-[#F4F7FA] py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="text-[#2A9D8F] text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
            Resources
          </span>
          <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-bold text-[#0D1B2A] tracking-tight mb-6">
            The Knowledge Hub
          </h2>
        </div>

        {/* Resource Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {resources.map((resource, index) => (
            <div
              key={index}
              data-testid={`resource-card-${index}`}
              className="bg-white border border-[#8BA4BE]/20 p-8 rounded-none shadow-sm hover:shadow-md hover:-translate-y-1 transition-all group cursor-pointer"
            >
              <resource.icon className="w-10 h-10 text-[#2A9D8F] mb-4" />
              <h3 className="font-outfit text-lg font-bold text-[#0D1B2A] mb-3">{resource.title}</h3>
              <p className="text-[#162032] text-sm leading-relaxed mb-4">{resource.description}</p>
              <span className="text-[#F5A623] font-bold text-sm group-hover:underline">{resource.cta}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Call to Action Section
const CTASection = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentForm, setCurrentForm] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    message: "",
  });

  const ctaCards = [
    {
      icon: Building2,
      title: "For Infrastructure Operators",
      description: "Register your network. Get matched with elite Guard cyber defenders. It's free, PCII-protected, and your community deserves it.",
      cta: "Register Now",
      ctaColor: "bg-[#F5A623] hover:bg-[#F5A623]/90 text-[#0D1B2A]",
      formType: "operator",
    },
    {
      icon: Mail,
      title: "For Advocates",
      description: "Download our toolkit. Contact your representatives. Share the mission. Closing the disparity gap starts with awareness.",
      cta: "Get the Toolkit",
      ctaColor: "bg-[#2A9D8F] hover:bg-[#2A9D8F]/90",
      formType: "advocate",
    },
    {
      icon: Heart,
      title: "For Donors",
      description: "Critical Guard is a 501(c)(3) nonprofit. Your contribution funds outreach, platform development, and Guard unit readiness programs.",
      cta: "Support the Mission",
      ctaColor: "bg-white hover:bg-white/90 text-[#0D1B2A]",
      formType: "donor",
    },
  ];

  const handleOpenModal = (formType) => {
    setCurrentForm(formType);
    setFormSubmitted(false);
    setFormData({ name: "", email: "", organization: "", message: "" });
    setModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    toast.success("Thank you for your submission! We'll be in touch soon.");
  };

  const getFormTitle = () => {
    switch (currentForm) {
      case "operator": return "Register Your Infrastructure";
      case "advocate": return "Get the Advocacy Toolkit";
      case "donor": return "Support the Mission";
      default: return "Contact Us";
    }
  };

  return (
    <section id="get-involved" data-testid="cta-section" className="bg-[#0D1B2A] border-t-2 border-[#F5A623] py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tighter mb-6">
            Defend What Matters.
          </h2>
          <p className="text-[#8BA4BE] text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Every water system, every power grid, every community — defended by the same elite 
            National Guard cyber teams that protect federal networks. Join the mission.
          </p>
        </div>

        {/* CTA Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {ctaCards.map((card, index) => (
            <div
              key={index}
              data-testid={`cta-card-${index}`}
              className="bg-[#162032] border border-[#8BA4BE]/15 p-8 rounded-none text-center"
            >
              <card.icon className="w-12 h-12 text-[#F5A623] mx-auto mb-4" />
              <h3 className="font-outfit text-xl font-bold text-white mb-4">{card.title}</h3>
              <p className="text-[#8BA4BE] text-sm leading-relaxed mb-6">{card.description}</p>
              <button
                onClick={() => handleOpenModal(card.formType)}
                data-testid={`cta-button-${index}`}
                className={`inline-flex items-center justify-center px-8 py-4 ${card.ctaColor} font-extrabold text-sm uppercase tracking-wider rounded-none transition-all`}
              >
                {card.cta}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Form Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#0D1B2A] border border-[#8BA4BE]/30 text-white max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-outfit text-2xl font-bold text-white">{getFormTitle()}</DialogTitle>
            <DialogDescription className="text-[#8BA4BE]">
              Fill out the form below and we'll get back to you shortly.
            </DialogDescription>
          </DialogHeader>
          
          {formSubmitted ? (
            <div className="py-8 text-center" data-testid="cta-form-success">
              <div className="w-16 h-16 bg-[#2A9D8F]/20 border border-[#2A9D8F] rounded-none flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#2A9D8F]" />
              </div>
              <h3 className="font-outfit text-xl font-bold text-white mb-2">Thank You!</h3>
              <p className="text-[#8BA4BE]">Your submission has been received. We'll be in touch soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="cta-contact-form">
              <div>
                <label className="text-sm font-bold uppercase tracking-wider text-[#8BA4BE] mb-2 block">Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-[#162032] border border-[#8BA4BE]/30 text-white placeholder:text-[#8BA4BE]/50 focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623] rounded-none p-4"
                  placeholder="Your name"
                  data-testid="cta-form-input-name"
                />
              </div>
              <div>
                <label className="text-sm font-bold uppercase tracking-wider text-[#8BA4BE] mb-2 block">Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-[#162032] border border-[#8BA4BE]/30 text-white placeholder:text-[#8BA4BE]/50 focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623] rounded-none p-4"
                  placeholder="your@email.com"
                  data-testid="cta-form-input-email"
                />
              </div>
              <div>
                <label className="text-sm font-bold uppercase tracking-wider text-[#8BA4BE] mb-2 block">Organization</label>
                <input
                  type="text"
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  className="w-full bg-[#162032] border border-[#8BA4BE]/30 text-white placeholder:text-[#8BA4BE]/50 focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623] rounded-none p-4"
                  placeholder="Your organization (optional)"
                  data-testid="cta-form-input-organization"
                />
              </div>
              <div>
                <label className="text-sm font-bold uppercase tracking-wider text-[#8BA4BE] mb-2 block">Message</label>
                <textarea
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full bg-[#162032] border border-[#8BA4BE]/30 text-white placeholder:text-[#8BA4BE]/50 focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623] rounded-none p-4 resize-none"
                  placeholder="Tell us more about your needs..."
                  data-testid="cta-form-input-message"
                />
              </div>
              <button
                type="submit"
                data-testid="cta-form-submit"
                className="w-full inline-flex items-center justify-center px-8 py-4 bg-[#F5A623] text-[#0D1B2A] font-extrabold text-sm uppercase tracking-wider hover:bg-[#F5A623]/90 transition-all rounded-none"
              >
                Submit
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

// Footer Section
const Footer = () => {
  const quickLinks = [
    { label: "The Threat", href: "#threat" },
    { label: "Our Solution", href: "#solution" },
    { label: "Partnership Network", href: "#partnership" },
    { label: "Policy & Advocacy", href: "#policy" },
    { label: "Resources", href: "#resources" },
    { label: "Get Involved", href: "#get-involved" },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer data-testid="footer" className="bg-[#0D1B2A] border-t border-[#F5A623]/30 py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 mb-12">
          {/* About */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="Critical Guard" className="h-12 w-auto" />
              <span className="font-outfit font-black text-white text-lg tracking-wider">CRITICAL GUARD</span>
            </div>
            <p className="text-[#8BA4BE] text-sm leading-relaxed mb-4">
              A nonprofit clearinghouse operationalizing the Digital National Guard Framework. 
              We bridge the gap between local vulnerability and national defense — deploying 
              America's elite cyber defenders where they're needed most.
            </p>
            <p className="text-[#8BA4BE] text-xs">501(c)(3) Nonprofit · EIN: [PENDING]</p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-outfit font-bold text-white text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-[#8BA4BE] text-sm hover:text-[#F5A623] transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-outfit font-bold text-white text-lg mb-4">Contact & Connect</h4>
            <div className="space-y-3 text-[#8BA4BE] text-sm">
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                info@criticalguard.org
              </p>
              <p className="flex items-center gap-2">
                <Landmark className="w-4 h-4" />
                Washington, D.C.
              </p>
            </div>
            <div className="flex gap-4 mt-4">
              <a href="#" className="text-[#8BA4BE] hover:text-[#F5A623] transition-colors" aria-label="Twitter">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="#" className="text-[#8BA4BE] hover:text-[#F5A623] transition-colors" aria-label="LinkedIn">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="#" className="text-[#8BA4BE] hover:text-[#F5A623] transition-colors" aria-label="GitHub">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#8BA4BE]/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-[#8BA4BE] text-xs text-center md:text-left">
            © 2026 Critical Guard. All rights reserved. | Privacy Policy | Terms of Use | 501(c)(3) Public Charity
          </div>
          <div className="text-[#F5A623] text-sm font-bold tracking-wider">
            Local Infrastructure. National Strength.
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const sections = ["threat", "solution", "partnership", "policy", "resources", "get-involved"];
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-50% 0px -50% 0px" }
    );

    sections.forEach((section) => {
      const element = document.getElementById(section);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  // Enable smooth scrolling
  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = "auto";
    };
  }, []);

  return (
    <div className="App font-inter">
      <Toaster position="top-right" richColors />
      <Navigation activeSection={activeSection} />
      <main>
        <HeroSection />
        <ImpactStatsBar />
        <ProblemSection />
        <SolutionSection />
        <StakeholderSection />
        <PolicySection />
        <ResourcesSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
